import React, {
  useState,
  ChangeEvent,
  useMemo,
  createContext,
  useContext
} from 'react';
import { ColumnDef, Row } from '@tanstack/react-table';
import AdvanceTable from 'components/base/AdvanceTable';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import RevealDropdown from 'components/base/RevealDropdown';
import {
  CustomerList,
  blockUnblockCustomer
} from 'services/hvaservicecampaign.service';
import {
  Dropdown,
  Modal,
  Form,
  Button as BootstrapButton
} from 'react-bootstrap';
import Button from 'components/base/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserLock } from '@fortawesome/free-solid-svg-icons';
import Badge from 'components/base/Badge';
import FeatherIcon from 'feather-icons-react';
import SearchBox from 'components/common/SearchBox';
import FilterTab from 'components/common/FilterTab';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';

interface TableProps {
  customers: CustomerList[];
  onCustomersChange: () => Promise<void>;
  campaignId: number;
}

type CustomerListActionCellProps = {
  record_id: number;
  campaign_id: number;
  nombre: string;
  blocked: string;
};

const CustomersChangeContext = createContext<(() => Promise<void>) | undefined>(
  undefined
);

const useCustomersChange = () => {
  const context = useContext(CustomersChangeContext);
  if (!context) {
    throw new Error(
      'useCustomersChange must be used within a CustomersChangeProvider'
    );
  }
  return context;
};

const CustomerListActionCell = ({
  record_id,
  campaign_id,
  nombre,
  blocked
}: CustomerListActionCellProps) => {
  const [showEditModal, setShowEditModal] = useState(false);
  const [isBlocked, setIsBlocked] = useState(blocked === 'Blocked');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const onCustomersChange = useCustomersChange();

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setError(null);
    setIsBlocked(blocked === 'Blocked');
  };

  const handleShowEditModal = () => {
    setShowEditModal(true);
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setIsBlocked(e.target.checked);
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      await blockUnblockCustomer({
        campaign_id,
        record_id,
        operation: isBlocked ? 'block' : 'unblock'
      });

      setShowEditModal(false);
      await onCustomersChange();
    } catch (err) {
      setError('Error updating customer status. Please try again.');
      console.error('Error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <RevealDropdown btnClassName="p-2 pt-1 pb-1 fs-11">
        <Dropdown.Item onClick={handleShowEditModal}>
          <FontAwesomeIcon icon={faUserLock} className="fs-8 p-2 pt-0 pb-0" />
          Block / Unblock
        </Dropdown.Item>
      </RevealDropdown>

      <Modal show={showEditModal} onHide={handleCloseEditModal} centered>
        <Form onSubmit={handleFormSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>Block or Unblock a customer</Modal.Title>
          </Modal.Header>
          <Modal.Body className="px-6 pt-4">
            <p className="fs-9">
              <span className="fw-bold">Record:</span> {record_id}
              <br />
              <span className="fw-bold">BXP Campaign:</span> {campaign_id}
              <br />
              <span className="fw-bold">Name:</span> {nombre}
              <br />
              <span className="fw-bold">Status:</span> {blocked}
              <br />
              <div className="form-check form-switch mb-0 mt-2 border rounded">
                <div className="p-3">
                  <label className="form-check-label">
                    <input
                      type="checkbox"
                      name="block"
                      value="block"
                      checked={isBlocked}
                      onChange={handleInputChange}
                      className="form-check-input"
                      disabled={isLoading}
                    />
                    {isBlocked ? ' Block ' : ' Unblock '} this customer
                  </label>
                </div>
              </div>
            </p>
            {error && (
              <div className="alert alert-danger mt-3 mb-0 py-2 fs-9">
                {error}
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={handleCloseEditModal}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <BootstrapButton
              variant="primary"
              type="submit"
              disabled={isLoading}
            >
              {isLoading ? 'Processing...' : 'Apply'}
            </BootstrapButton>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export const CustomerListTable: React.FC<TableProps> = ({
  customers,
  onCustomersChange
}) => {
  const columns = useMemo<ColumnDef<CustomerList>[]>(() => {
    // Columnas fijas
    const fixedColumns: ColumnDef<CustomerList>[] = [
      {
        accessorKey: 'record_id',
        header: 'ID',
        cell: ({ row: { original } }: { row: Row<CustomerList> }) =>
          original.record_id,
        meta: {
          headerProps: { style: { width: '5%' }, className: 'pe-2 ps-0 fs-10' },
          cellProps: { className: 'py-0 pe-2 ps-0 white-space-nowrap' }
        }
      },
      {
        accessorKey: 'campaign_id',
        header: 'Campaign',
        cell: ({ row: { original } }: { row: Row<CustomerList> }) =>
          original.campaign_id,
        meta: {
          headerProps: { style: { width: '5%' }, className: 'px-1 fs-10' },
          cellProps: { className: 'px-1 white-space-nowrap text-center' }
        }
      },
      {
        accessorKey: 'blocked',
        header: 'Status',
        accessorFn: ({ blocked }: CustomerList) => blocked,
        filterFn: (
          row: Row<CustomerList>,
          columnId: string,
          filterValue: string
        ) => {
          if (!filterValue || filterValue === 'all') return true;
          return row.getValue(columnId) === filterValue;
        },
        cell: ({ row: { original } }: { row: Row<CustomerList> }) => {
          return (
            <Badge
              variant="phoenix"
              bg={original.blocked === 'Blocked' ? 'danger' : 'success'}
              iconPosition="end"
              icon={
                <FeatherIcon
                  icon={original.blocked === 'Blocked' ? 'phone-off' : 'phone'}
                  size={12}
                  className="ms-1"
                />
              }
            >
              {original.blocked === 'Blocked' ? 'Blocked' : 'Active'}
            </Badge>
          );
        },
        meta: {
          headerProps: { style: { width: '5%' }, className: 'px-1 fs-10' },
          cellProps: { className: 'px-1 white-space-nowrap' }
        }
      },
      {
        accessorKey: 'action',
        header: '',
        enableSorting: false,
        cell: ({ row: { original } }: { row: Row<CustomerList> }) => (
          <CustomerListActionCell {...original} />
        ),
        meta: {
          headerProps: { style: { width: '5%' } }
        }
      },
      {
        accessorKey: 'nombre',
        header: 'Name',
        enableSorting: true,
        cell: ({ row: { original } }: { row: Row<CustomerList> }) =>
          original.nombre,
        meta: {
          headerProps: { style: { width: '5%' }, className: 'px-1 fs-10' },
          cellProps: { className: 'px-1 white-space-nowrap' }
        }
      }
    ];

    // Si no hay datos, devolvemos solo las columnas fijas
    if (customers.length === 0) return fixedColumns;

    try {
      // Tomamos el primer registro para analizar la estructura del objeto
      const firstRecord = customers[0];
      const customerData = firstRecord.customer_data || {};

      // Obtenemos las keys del primer nivel del objeto
      const jsonKeys = Object.keys(customerData);

      // Creamos una columna para cada key
      const dynamicColumns: ColumnDef<CustomerList>[] = jsonKeys.map(key => ({
        accessorFn: (row: CustomerList) => {
          const data = row.customer_data || {};
          return data[key];
        },
        header: key,
        // eslint-disable-next-line
        cell: ({ getValue }: { getValue: () => any }) => {
          const value = getValue();
          // Si el valor es un objeto o array, lo convertimos a string
          if (typeof value === 'object' && value !== null) {
            return JSON.stringify(value);
          }
          return value?.toString() || '';
        },
        meta: {
          headerProps: { className: 'px-1 fs-10' },
          cellProps: { className: 'px-1 white-space-nowrap' }
        }
      }));

      return [...fixedColumns, ...dynamicColumns];
    } catch (error) {
      console.error('Error creating dynamic columns:', error);
      return fixedColumns;
    }
  }, [customers]);

  const table = useAdvanceTable({
    data: customers,
    columns,
    pageSize: 18,
    pagination: true,
    sortable: true,
    selection: false
  });

  const handleFilterItemClick = useMemo(
    () => (columnId: string, value: string) => {
      const column = table.getColumn(columnId);
      column?.setFilterValue(value === 'all' ? '' : value);
    },
    [table]
  );

  const tabItems = useMemo(() => {
    const getDataCount = (status: string) =>
      customers.filter(customer => customer.blocked === status).length;

    return [
      {
        label: 'All',
        value: 'all',
        onClick: () => handleFilterItemClick('blocked', 'all'),
        count: customers.length
      },
      {
        label: 'Active',
        value: 'Unblocked',
        onClick: () => handleFilterItemClick('blocked', 'Unblocked'),
        count: getDataCount('Unblocked')
      },
      {
        label: 'Blocked',
        value: 'Blocked',
        onClick: () => handleFilterItemClick('blocked', 'Blocked'),
        count: getDataCount('Blocked')
      }
    ];
  }, [customers, handleFilterItemClick]);

  const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    table.setGlobalFilter(e.target.value || undefined);
  };

  return (
    <CustomersChangeContext.Provider value={onCustomersChange}>
      <AdvanceTableProvider {...table}>
        <div className="mb-2 pt-3">
          <div className="d-flex flex-wrap gap-3 justify-content-between align-items-center">
            <div className="d-flex flex-wrap gap-3">
              <SearchBox
                placeholder="Search..."
                onChange={handleSearchInputChange}
              />
              <FilterTab tabItems={tabItems} className="pt-3" />
            </div>
            <Button variant="link" onClick={onCustomersChange} className="p-0">
              <FeatherIcon icon="refresh-cw" size={14} className="me-2" />
              Refresh
            </Button>
          </div>
        </div>
        <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 border-translucent position-relative top-1 pb-3">
          <AdvanceTable
            tableProps={{
              size: 'sm',
              className: 'phoenix-table fs-9'
            }}
            rowClassName="hover-actions-trigger btn-reveal-trigger position-static"
          />
          <AdvanceTableFooter pagination />
        </div>
      </AdvanceTableProvider>
    </CustomersChangeContext.Provider>
  );
};
